import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Modal, Button } from "@/components/ui";
import { Copy, Mail } from "lucide-react";
import { toast } from "sonner";

export function LetterModal({ eob, onClose }) {
  const { user } = useAuth();
  const [showEmail, setShowEmail] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (eob) setShowEmail(false);
  }, [eob]);

  const copy = async () => { try { await navigator.clipboard.writeText(eob?.appeal_letter || ""); toast.success("Copied to clipboard"); } catch { toast.error("Copy failed"); } };

  const sendEmail = async () => {
    setBusy(true);
    try {
      const r = await api.emailAppeal(eob.id);
      if (r.email_sent) {
        toast.success(`Appeal PDF emailed to ${r.sent_to || user?.email || "your inbox"}`);
        setShowEmail(false);
      } else {
        toast.error(r.reason || "Email couldn't be sent right now — please try again shortly.");
      }
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not send email");
    } finally { setBusy(false); }
  };

  return (
    <Modal open={!!eob} onClose={onClose} title="Appeal letter" testid="letter-modal">
      <div className="text-xs text-subtle mb-3">
        {eob?.appeal_drafted_by ? `Drafted by ${eob.appeal_drafted_by} · ` : ""}{eob?.appeal_drafted_at ? new Date(eob.appeal_drafted_at).toLocaleString() : ""}
      </div>
      <div className="max-h-72 overflow-y-auto text-sm text-ink whitespace-pre-line leading-relaxed border border-line rounded-lg p-4 bg-white" data-testid="letter-body">
        {eob?.appeal_letter}
      </div>

      {showEmail ? (
        <div className="mt-4 border border-line rounded-lg p-3 bg-brand-tint" data-testid="email-appeal-form">
          <p className="text-sm text-ink mb-3">We&apos;ll email a PDF copy to your account address: <strong data-testid="email-recipient">{user?.email}</strong></p>
          <div className="flex gap-2">
            <Button data-testid="send-appeal-email" onClick={sendEmail} loading={busy} className="flex-1"><Mail className="w-4 h-4" /> Send PDF</Button>
            <Button variant="ghost" data-testid="cancel-email" onClick={() => setShowEmail(false)}>Cancel</Button>
          </div>
          <p className="text-xs text-subtle mt-2">For your records — sent to you, not the insurer.</p>
        </div>
      ) : (
        <div className="flex gap-2 mt-4">
          <Button data-testid="copy-letter-button" onClick={copy} variant="secondary" className="flex-1"><Copy className="w-4 h-4" /> Copy</Button>
          <Button data-testid="email-appeal-button" onClick={() => setShowEmail(true)} className="flex-1"><Mail className="w-4 h-4" /> Email as PDF</Button>
        </div>
      )}
      <p className="text-xs text-subtle text-center mt-3">Human review required before sending. Not legal advice.</p>
    </Modal>
  );
}
